// Approvals, one source for every screen (phone Approvals + Home, web Approvals + Home). The server decides an
// approval, so when there's signal its list (agentApprovals) is the truth — refreshed every 20 s while a screen shows
// it; the reminder docs it files (synced, on the device) cover no signal. Deciding is a server call.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { approvalsView, approvalSessions } from '@battleops/shared';
import { useLocalCollection } from './useLocal';
import { call } from '../session/session';
import { getSync } from '../session/boot';

const REFRESH_MS = 20000;

export function useApprovals({ online = true, live = true } = {}) {
  const reminders = useLocalCollection('reminders') || [];
  const [server, setServer] = useState(null);
  const [decided, setDecided] = useState({});   // approval id → verdict, until the server / sync says so

  const reload = useCallback(async () => {
    try { const r = await call('agentApprovals', { action: 'list', status: 'all', limit: 50 }); if (Array.isArray(r?.approvals)) setServer(r.approvals); }
    catch { /* no signal: the reminder docs stand in */ }
  }, []);
  useEffect(() => {
    if (!online) return undefined;
    reload();
    if (!live) return undefined;
    const iv = setInterval(reload, REFRESH_MS);
    return () => clearInterval(iv);
  }, [online, live, reload]);

  const view = useMemo(() => approvalsView({ reminders, server, decided }), [reminders, server, decided]);

  /** approve | reject. Throws a message for the card; "already decided" elsewhere just drops it from Waiting. */
  const decide = useCallback(async (id, action, note) => {
    try {
      await call('agentApprovals', { action, id, note: (note || '').trim() || undefined });
      setDecided((d) => ({ ...d, [id]: action === 'approve' ? 'approved' : 'rejected' }));
    } catch (e) {
      const msg = String(e?.message || e);
      if (/not pending|already/i.test(msg)) setDecided((d) => ({ ...d, [id]: 'decided' }));
      else throw new Error(e?.status ? msg : 'No connection — try again when you have signal.');
    } finally {
      reload();
      getSync()?.syncNow?.().catch?.(() => {});
    }
  }, [reload]);

  /** Approve a working session's approvals in one go, oldest first; stops at the first that fails. */
  const approveAll = useCallback(async (ids) => {
    try {
      const r = await call('agentApprovals', { action: 'approveMany', ids });
      const ok = (r?.approvals || []).filter((a) => a.status !== 'failed').map((a) => a.id);
      setDecided((d) => ({ ...d, ...Object.fromEntries(ok.map((id) => [id, 'approved'])) }));
      if (r?.error) throw new Error(`Stopped: ${r.error}`);
    } catch (e) { throw new Error(e?.status || /^Stopped/.test(String(e?.message)) ? String(e.message || e) : 'No connection — try again when you have signal.'); }
    finally { reload(); getSync()?.syncNow?.().catch?.(() => {}); }
  }, [reload]);

  /** Approve it and let this agent do this kind of action again without asking (in the same books). Revocable. */
  const alwaysAllow = useCallback(async (item) => {
    const match = item?.args && typeof item.args === 'object' && item.args.booksetId ? { booksetId: item.args.booksetId } : {};
    try {
      await call('agentApprovals', { action: 'alwaysAllow', id: item.id, match });
      setDecided((d) => ({ ...d, [item.id]: 'approved' }));
    } catch (e) { throw new Error(e?.status ? String(e.message || e) : 'No connection — try again when you have signal.'); }
    finally { reload(); getSync()?.syncNow?.().catch?.(() => {}); }
  }, [reload]);

  // The standing "always allow" rules (agent + action), to see and revoke.
  const [rules, setRules] = useState(null);
  const loadRules = useCallback(async () => { try { const r = await call('agentApprovals', { action: 'rules' }); setRules(r?.rules || []); } catch { /* offline */ } }, []);
  const revokeRule = useCallback(async (ruleId) => { await call('agentApprovals', { action: 'revokeRule', ruleId }); await loadRules(); }, [loadRules]);

  const sessions = useMemo(() => approvalSessions(view.waiting), [view.waiting]);
  return { ...view, sessions, decide, approveAll, alwaysAllow, rules, loadRules, revokeRule, reload, fromServer: server != null };
}
